import React, { useEffect, useState } from "react";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { deleteCharityLogo, fetchCharities, updateCharity } from "@services/charityService";
import { filterByNameOrMessage } from "@util/filter";
import ActivateConfirmationPopup from "@components/modals/ActivateConfirmationPopup/ActivateConfirmationPopup";
import DeactivateConfirmationPopup from "@components/modals/DeactivateConfirmationPopup/DeactivateConfirmationPopup";
import { Status } from "@models/Common";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { regionBadge } from "@util/domain";

const RESULTS_PER_PAGE = 8;

const CharitiesPage: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [charities, setCharities] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);
    const [showActivatePopup, setShowActivatePopup] = useState(false);
    const [selectedCharityId, setSelectedCharityId] = useState("");

    // Fetch charities on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchCharities();
                setCharities(data);
            } catch (err) {
                setError("Failed to fetch charities.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    // Filter and paginate charities
    const filteredData = filterByNameOrMessage(charities, searchTerm);
    const totalPages = Math.ceil(filteredData.length / RESULTS_PER_PAGE);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    const handleSetCharityStatus = async (id: string, status: Status) => {
        try {
            setLoading(true);
            await updateCharity(id, { status });
            // Update charity status locally
            setCharities((prevState) => {
                const newState = [...prevState];
                const index = prevState.findIndex((val) => val.id === id);
                if (index > -1) {
                    newState[index].status = status;
                }
                return newState;
            });
        } catch {
            setError("Failed to set charity status.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCharityLogo = async (id: string) => {
        try {
            setLoading(true);
            await deleteCharityLogo(id);
            // Update charity status locally
            setCharities((prevState) => {
                const newState = [...prevState];
                const index = prevState.findIndex((val) => val.id === id);
                if (index > -1) {
                    newState[index].logo = undefined;
                }
                return newState;
            });
        } catch {
            setError("Failed to delete charity logo.");
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async () => {
        setShowActivatePopup(false);
        await handleSetCharityStatus(selectedCharityId, "active");
    };

    const handleDeactivate = async () => {
        setShowDeactivatePopup(false);
        await handleSetCharityStatus(selectedCharityId, "deactivated");
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (paginatedData.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No charities ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        return (
            <div className={commonStyles.itemList}>
                {paginatedData.map((data) => (
                    <InfoCard
                        key={data.id}
                        title={data.name}
                        badge={regionBadge(data.region)}
                        media={{
                            type: "image",
                            src: data.logo,
                        }}
                        items={[data.isPetCharity ? "Pet Charity" : ""]}
                        buttons={[
                            {
                                title: "Edit",
                                variant: "secondary",
                                to: `editor?${new URLSearchParams({
                                    mode: "edit",
                                    id: data.id,
                                }).toString()}`,
                            },
                            {
                                title: "Delete Logo",
                                variant: "secondary",
                                onClick: async () => {
                                    await handleDeleteCharityLogo(data.id);
                                },
                            },
                            {
                                title: data.status === "active" ? "Deactivate" : "Activate",
                                variant: data.status === "active" ? "cancel" : "primary",
                                onClick: () => {
                                    setSelectedCharityId(data.id);
                                    if (data.status === "active") {
                                        setShowDeactivatePopup(true);
                                    } else {
                                        setShowActivatePopup(true);
                                    }
                                },
                            },
                        ]}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                <h1>Charities</h1>
                {!loading && !error && (
                    <div className={commonStyles.buttonGroup}>
                        <Button variant="primary" size="medium" to="editor?mode=add">
                            Add
                        </Button>
                    </div>
                )}
            </div>
            {!loading && (
                <div className={commonStyles.searchBar}>
                    <SearchBar
                        value={searchTerm}
                        onChange={(val) => setSearchTerm(val)}
                    />
                </div>
            )}
            {renderContent()}
            {totalPages > 1 && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
            <ActivateConfirmationPopup
                visible={showActivatePopup}
                onConfirm={handleActivate}
                onCancel={() => setShowActivatePopup(false)}
            />
            <DeactivateConfirmationPopup
                visible={showDeactivatePopup}
                onConfirm={handleDeactivate}
                onCancel={() => setShowDeactivatePopup(false)}
            />
        </div>
    );
};

export default CharitiesPage;
